import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import AOS from "aos";
import "aos/dist/aos.css";
import Button from "../Components/Button";
import agribanner from "../images/agri/hero-banner.png";
import cardBoundaries from "../images/agri/card-boundaries.png";
import cardIdleLand from "../images/agri/card-idle-land.png";
import cardDocuments from "../images/agri/card-documents.png";
import cardNoAccount from "../images/agri/card-no-account.png";
import serviceCustodial from "../images/agri/service-custodial.png";
import serviceDocumentation from "../images/agri/service-documentation.png";
import serviceLease from "../images/agri/service-lease.png";
import serviceCultivation from "../images/agri/service-cultivation.png";
import serviceYield from "../images/agri/service-yield.png";
import stepTellUs from "../images/agri/step-1-tell-us.svg";
import stepAudit from "../images/agri/step-2-audit.svg";
import stepPlan from "../images/agri/step-3-plan.svg";
import stepManage from "../images/agri/step-4-manage.svg";
import stepReport from "../images/agri/step-5-report.svg";
import serviceRadiusMap from "../images/agri/service-radius-map.png";
import chooseSimplicity from "../images/liaison/simplicity.png";
import chooseExpertise from "../images/liaison/expertise.png";
import chooseVisibility from "../images/liaison/Visibility.png";
import chooseSaveTime from "../images/liaison/save_time.png";
import FAQ from "../Components/FAQ/FAQ";
import FormModal from "../Components/FormModal";
import closeIcon from "../images/close_icon.png";

// TODO: placeholder (footer number) - replace with the confirmed WhatsApp business number, digits only with country code
const WHATSAPP_NUMBER = "917418535050";

// Google Apps Script web-app endpoint for the "Agri-Property WhatsApp Enquiries" sheet
const AGRI_GSHEET_URL = "https://script.google.com/macros/s/AKfycbxNV7wM7lAOo0T_i9ZoOmX3O1URyA8EHyQ9zmEgGHFimQqIdALR-LyChkCWAg2F-VTA/exec";

const processSteps = [
    {
        icon: stepTellUs,
        title: "Tell us about your land",
        description: "Share the location, size and what's worrying you. A call or a WhatsApp message is enough to begin."
    },
    {
        icon: stepAudit,
        title: "The Land Audit",
        description: "Our team visits, checks the boundary, notes the land's condition and reviews the documents you have."
    },
    {
        icon: stepPlan,
        title: "Choose your plan",
        description: "Based on the audit, we recommend the services your land needs, for you to choose."
    },
    {
        icon: stepManage,
        title: "We manage and monitor",
        description: "Our team carries out your chosen services, from site visits and document follow-ups to tenant coordination and advisory reviews. Anything urgent reaches you promptly, well before you would have noticed it yourself."
    },
    {
        icon: stepReport,
        title: "Your report, shared Digitally",
        description: "Photos and a written note, with one dedicated contact for your questions."
    }
];

const personas = [
    {
        title: "NRI landowners",
        description: "Limited annual visits leave your land unprotected. Caretaker updates are verbal and unverifiable, and inherited land records often stay in a previous owner's name."
    },
    {
        title: "City-based owners",
        description: "You live a few hours away but have no time for regular site checks. Fallow land earns nothing, and minor encroachments are only noticed on rare weekend visits."
    },
    {
        title: "Families who don't farm",
        description: "The ancestral property that needs you to be physically present to ensure the land stays as the same farming land like before."
    },
    {
        title: "Owners planning ahead",
        description: "You don't know what your land is worth, or how easily it would sell or lease. (or) You have nothing on record to show a buyer or tenant."
    },
    {
        title: "Institutions and trusts",
        description: "Managing scattered endowment or CSR land holdings. You need formal board accountability, audit trails, and legal protection against squatters."
    }
];

const chooseus = [
    {
        src: chooseSimplicity,
        alt: "Simplicity",
        heading: "Simplicity",
        content: "One accountable partner for site visits, documents and tenants, so you always know where your land stands."
    },
    {
        src: chooseExpertise,
        alt: "Expertise",
        heading: "Expertise",
        content: "Deep experience in land records, liaison and on-ground care across agricultural holdings."
    },
    {
        src: chooseVisibility,
        alt: "Visibility",
        heading: "Visibility",
        content: "A verified report with photos and updates after every visit, wherever you live."
    },
    {
        src: chooseSaveTime,
        alt: "Save Time",
        heading: "Save Time",
        content: "Scheduled visits and follow-ups handled for you, with no chasing caretakers for news."
    }
];

const whyChooseBoxes = [
    {
        title: "Complete real estate services under one roof",
        description: "Across buying, selling, property management, registration and liaison."
    },
    {
        title: "In-house liaison and legal teams",
        description: "Documentation issues are handled by people who do this every day."
    },
    {
        title: "An on-ground team with a technology-integrated workflow",
        description: "Every visit is logged and nothing depends on memory."
    }
];

const services = [
    {
        image: serviceCustodial,
        title: "Custodial Land Care",
        description: "Scheduled site visits, photo and video checks, GPS boundary mapping, and supervision of caretakers, so you always know the state of your land.",
        buttonLabel: "Enquire Custodial Care"
    },
    {
        image: serviceDocumentation,
        title: "Documentation & Compliance",
        description: "We track your patta, chitta and FMB records, flag gaps early, and bring in our liaison team when something needs fixing.",
        buttonLabel: "Enquire Documentation"
    },
    {
        image: serviceLease,
        title: "Lease Facilitation",
        description: "We find and vet tenants, put a proper written lease in place, and follow up on payments, so your land earns without the worry.",
        buttonLabel: "Enquire Leasing"
    },
    {
        image: serviceCultivation,
        title: "Cultivation & Plantation Advisory",
        description: "Expert advice and supervision on what to grow and how it's managed, through partner agronomists. We advise, we don't farm your land.",
        buttonLabel: "Enquire Cultivation"
    },
    {
        image: serviceYield,
        title: "Asset Liquidity & Rental-Yield Advisory",
        description: "For owners weighing their options: we assess how readily your land could be sold or leased, and the rental yield it could realistically earn.",
        buttonLabel: "Enquire Yield Advisory"
    }
];

const problemCards = [
    {
        image: cardBoundaries,
        title: "Boundaries you can't see",
        description: "Encroachment often starts small, a shifted fence, a new path. By the time you visit, it's harder to set right."
    },
    {
        image: cardIdleLand,
        title: "Land sitting idle",
        description: "Fallow land earns nothing and can quietly lose value, while the right tenant or plan goes unexplored."
    },
    {
        image: cardNoAccount,
        title: "No clear account",
        description: "Caretakers and tenants rarely report in writing. You hear that things are fine, but you can't see it for yourself."
    },
    {
        image: cardDocuments,
        title: "Documents in doubt",
        description: "Patta, chitta and FMB records fall out of date, especially after inheritance or partition. Gaps surface when you least expect them."
    },
];

export default function Agri_Property() {

    useEffect(() => {
        AOS.init({ duration: 1000 });
    }, []);

    // Enquiry modal state - shared by all CTA buttons on this page
    const [modalToggle, setModalToggle] = useState(false);
    function toggleModal() {
        setModalToggle(!modalToggle);
    }

    // WhatsApp modal state - "talk" (hero) or "register" (where we operate), null when closed
    // UI replicated from ChatusForm.js; no backend for now - formValues are consoled, then redirect to WhatsApp
    const waInitialValues = { name: "", phonenumber: "", location: "" };
    const [waModal, setWaModal] = useState(null);
    const [waFormValues, setWaFormValues] = useState(waInitialValues);
    const [waFormErrors, setWaFormErrors] = useState({});

    function closeWaModal() {
        setWaModal(null);
        setWaFormValues(waInitialValues);
        setWaFormErrors({});
    }

    const handleWaChange = (e) => {
        const { name, value } = e.target;
        setWaFormValues({ ...waFormValues, [name]: value });
    };

    // Error hiding function (UX Improvement)
    const handleWaFocus = (e) => {
        const { name } = e.target;
        if (waFormErrors[name]) {
            setWaFormErrors((prev) => {
                const updatedErrors = { ...prev };
                delete updatedErrors[name];
                return updatedErrors;
            });
        }
    };

    const validateWa = (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = "Name is Required";
        }
        if (!values.phonenumber) {
            errors.phonenumber = "Phone Number is Required";
        } else if (isNaN(values.phonenumber)) {
            errors.phonenumber = "Invalid Phone Number";
        } else if (values.phonenumber.length !== 10) {
            errors.phonenumber = "Invalid Phone Number";
        }
        if (waModal === "register" && !values.location) {
            errors.location = "Location is Required";
        }
        return errors;
    };

    // Fire-and-forget write to the Google Sheet, same pattern as ChatusForm.handleGSheet
    const handleWaGSheet = (values) => {
        if (!AGRI_GSHEET_URL) return;
        fetch(AGRI_GSHEET_URL, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams(values).toString(),
        })
            .then((res) => res.text())
            .then((data) => {
                console.log("gsheet response", data);
            })
            .catch((error) => {
                console.log("gsheet error", error);
            });
    };

    const handleWaSubmit = (e) => {
        e.preventDefault();
        const error = validateWa(waFormValues);
        setWaFormErrors(error);
        if (Object.keys(error).length === 0) {
            const now = new Date();
            const enrichedValues = {
                ...waFormValues,
                enquiry_type: waModal === "talk" ? "Talk to us on WhatsApp" : "Register my location",
                page_data: "agri-propertymanagement",
                date_of_enquiry: now.toISOString().split("T")[0],
                time_of_enquiry: now.toTimeString().split(" ")[0],
            };
            console.log(enrichedValues);
            handleWaGSheet(enrichedValues);
            const message = waModal === "talk"
                ? `Hi, I am ${waFormValues.name}, I am looking for Agri-Property Management service.`
                : `Hi, I am ${waFormValues.name}, would like to enquire if you provide service at ${waFormValues.location}.`;
            setTimeout(() => {
                window.location.href = `https://wa.me/${WHATSAPP_NUMBER}/?text=${encodeURIComponent(message)}`;
            }, 500);
            closeWaModal();
        }
    };

    return (
        <>
            <Helmet>
                <title>Agricultural Property Management in Coimbatore | Lands & Lands</title>
                <meta
                    name="description"
                    content="Agricultural land management for landowners who can't be there themselves. Scheduled visits, verified reports, document care and lease facilitation across the Coimbatore periphery."
                />
            </Helmet>

            {/* Hero Section */}
            <section
                className="relative w-full bg-cover bg-center"
                style={{ backgroundImage: `url(${agribanner})` }}
                role="img"
                aria-label="Agricultural land managed by Lands & Lands"
            >
                <div className="max-w-[1440px] mx-auto flex items-end lg:items-center min-h-[480px] sm:min-h-[560px] lg:min-h-[640px] px-4 pt-12 pb-8 sm:px-8 sm:pt-32 sm:pb-12 lg:px-[100px] lg:py-16">

                    <div className="bg-white/90 border-l-[6px] border-agri-yellow w-full sm:max-w-[600px] lg:max-w-[675px] p-6 sm:py-[45px] sm:px-[35px]" data-aos="fade-up">
                        <h1 className="font-alibaba font-bold text-hblack text-[26px] leading-[1.3] sm:text-[36px] lg:text-[42px] lg:leading-[1.35]">
                            We represent you on the ground, for the agricultural land you can't visit.
                        </h1>
                        <p className="font-alibaba text-black text-[15px] sm:text-[17px] leading-relaxed mt-4 sm:mt-6 max-w-[560px]">
                            For agricultural landowners in Coimbatore, we manage your land on your behalf and
                            represent you on the ground, so it stays secure, well documented and productive,
                            with every activity verified and reported to you, wherever you live.
                        </p>
                        <div className="flex flex-col w-full sm:flex-row gap-4 sm:gap-6 mt-6 sm:mt-9">
                            <Button name={"Book a Site Visit"} design={"filled"} action={"modal-open"} modalfunction={toggleModal} />
                            <Button name={"Talk to us on WhatsApp"} design={"outline"} action={"modal-open"} modalfunction={() => setWaModal("talk")} />
                        </div>
                    </div>

                </div>
            </section>

            {/* Intro Statement & Problem Cards Section */}
            <section className="bg-grey py-[60px] sm:py-[100px]">
                <div className="max-w-[879px] mx-auto px-4">
                    <h2 className="heading" data-aos="fade-up">
                        The Cost of Distance
                    </h2>
                    <hr className="border-agri-yellow border-[2px] w-[87px] mx-auto my-[25px] sm:my-[38px]" data-aos="fade-up" data-aos-duration="800" />
                    <p className="font-alibaba text-font-grey text-center text-[16px] leading-relaxed sm:text-[20px] sm:leading-[1.5]" data-aos="fade-up">
                        Agricultural land held from afar is hard to protect and harder to put to good use.
                        Without someone on the ground, the care becomes guesswork, and its value can slip away unnoticed.
                    </p>
                </div>

                <div className="max-w-[1440px] mx-auto px-4 flex flex-wrap justify-center gap-[30px] mt-[40px] sm:mt-[60px]">
                    {problemCards.map((card, index) => (
                        <div
                            key={index}
                            className="w-full max-w-[304px] bg-white rounded-xl overflow-hidden shadow-[0px_4px_50px_rgba(0,0,0,0.25)]"
                            data-aos="fade-up"
                            data-aos-delay={index * 100}
                        >
                            <img className="w-full h-[201px] object-cover" src={card.image} alt={card.title} />
                            <div className="px-6 pt-6 pb-10 text-center">
                                <h3 className="font-noto font-bold text-hblack text-[22px] leading-[1.38] sm:text-[26px]">
                                    {card.title}
                                </h3>
                                <p className="font-alibaba text-font-grey text-[16px] leading-[1.53] sm:text-[17px] mt-6">
                                    {card.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

            </section>

            {/* Services Section */}
            <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-20 py-[60px] sm:py-[100px]">

                <div className="max-w-[722px] mx-auto mb-[40px] sm:mb-[60px]">
                    <h2 className="heading sm:!text-[40px] sm:!leading-[1.3]" data-aos="fade-up">
                        How We Can Help
                    </h2>
                    <hr className="border-agri-yellow border-[2px] w-[87px] mx-auto mt-[25px]" data-aos="fade-up" data-aos-duration="800" />
                </div>


                <div className="flex flex-wrap justify-center gap-[30px]">
                    {services.map((service, index) => (
                        <div
                            key={index}
                            className="flex flex-col w-full max-w-[406px] md:max-w-none md:w-[calc(50%-15px)] xl:w-[calc(33.333%-20px)] bg-[#FAF9F7] border border-[#E7E3DD] rounded-xl overflow-hidden shadow-[0px_4px_50px_rgba(0,0,0,0.25)]"
                            data-aos="fade-up"
                            data-aos-delay={(index % 3) * 100}
                        >
                            <img className="w-full h-[160px] sm:h-[180px] object-cover" src={service.image} alt={service.title} />
                            <div className="flex flex-col flex-grow gap-[18px] p-6">
                                <h3 className="font-karma font-medium text-[#1A1A1A] text-[20px] leading-[1.45] sm:text-[22px]">
                                    {service.title}
                                </h3>
                                <p className="font-alibaba text-[#524C45] text-[14px] leading-[1.57]">
                                    {service.description}
                                </p>
                                <div className="flex justify-center mt-auto pt-2">
                                    <Button
                                        name={"Enquire Now"}
                                        design={"filled"}
                                        action={"modal-open"}
                                        modalfunction={toggleModal}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Process Flow Section */}
            <section className="bg-grey py-[60px] sm:py-[100px] px-4 sm:px-8 lg:px-20">

                <div className="max-w-[722px] mx-auto mb-[40px] sm:mb-[60px]">
                    <h2 className="heading sm:!text-[40px] sm:!leading-[1.3]" data-aos="fade-up">
                        Our Approach
                    </h2>
                    <hr className="border-agri-yellow border-[2px] w-[87px] mx-auto mt-[25px]" data-aos="fade-up" data-aos-duration="800" />
                </div>

                <div className="relative max-w-[900px] mx-auto">
                    {/* Vertical flow line: left-aligned on phones, centered above iPad */}
                    <div className="absolute top-0 bottom-0 left-[28px] lg:left-1/2 lg:-translate-x-1/2 border-l-2 border-dashed border-[#D9D4CC]"></div>

                    {processSteps.map((step, index) => (
                        <div
                            key={index}
                            className="relative flex items-start gap-5 pb-12 last:pb-0 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-x-10 lg:pb-16"
                            data-aos="fade-up"
                        >
                            <img
                                className="relative z-10 w-[56px] h-[56px] shrink-0 lg:col-start-2 lg:row-start-1 lg:w-[72px] lg:h-[72px]"
                                src={step.icon}
                                alt=""
                            />
                            <div className={`lg:row-start-1 ${index % 2 === 0 ? "lg:col-start-1 lg:text-right" : "lg:col-start-3 lg:text-left"}`}>
                                <h3 className="font-noto font-bold text-hblack text-[20px] sm:text-[22px] leading-[1.4]">
                                    {step.title}
                                </h3>
                                <p className={`font-alibaba text-font-grey text-[16px] sm:text-[18px] leading-[1.39] mt-3 max-w-[420px] ${index % 2 === 0 ? "lg:ml-auto" : ""}`}>
                                    {step.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>

            </section>

            {/* Who It's For Section */}
            <section className="bg-white py-[60px] sm:py-[100px]">
                <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-20">

                    <div className="max-w-[722px] mx-auto mb-[40px] sm:mb-[60px]">
                        <h2 className="heading sm:!text-[35px] sm:!leading-[1.44]" data-aos="fade-up">
                            Is This For YOU?
                        </h2>
                        <hr className="border-agri-yellow border-[2px] w-[87px] mx-auto mt-[25px]" data-aos="fade-up" data-aos-duration="800" />
                    </div>

                    <div className="flex flex-wrap justify-center gap-8">
                        {personas.map((persona, index) => (
                            <div
                                key={index}
                                className="w-full max-w-[520px] md:max-w-none md:w-[calc(50%-16px)] bg-grey rounded-xl p-6 sm:p-8 transition-colors duration-200 ease-in-out hover:bg-yellow-50"
                                data-aos="fade-up"
                                data-aos-delay={(index % 2) * 100}
                            >
                                <h3 className="font-karma font-bold text-hblack text-[20px] sm:text-[22px] leading-[1.6]">
                                    {persona.title}
                                </h3>
                                <p className="font-inter text-font-grey text-[14px] leading-[1.57] mt-4">
                                    {persona.description}
                                </p>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {/* Why Choose Us Section */}
            <div className="max-w-full mx-auto bg-grey py-[60px] pb-[100px]" data-aos="fade-up">
                <h2 className="heading !font-noto">Why Lands &amp; Lands</h2>
                <hr className="border-agri-yellow border-[2px] w-[87px] mx-auto my-[25px]" />

                <div className="max-w-[1278px] mx-auto flex flex-wrap justify-between gap-y-10 mt-[40px] md:mt-[62px] px-2" data-aos="fade-up">
                    {chooseus.map((items, index) => (
                        <div key={index} className="w-[294px] h-[390px] mx-auto bg-white shadow-md py-[40px] sm:pt-[70px]">
                            <img className="w-[60px] h-[60px] mx-auto sm:w-[50px] sm:h-[50px]" src={items.src} alt={items.alt} />

                            <div className="max-w-[225px] mx-auto mt-[35px] text-center">
                                <h3 className="text-[26px] font-noto font-bold leading-[40px]">{items.heading}</h3>
                                <p className="font-alibaba text-[18px] text-[#5F5D5E] leading-[26px] font-normal mt-[20px]">{items.content}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Highlights strip */}
                <div className="max-w-[1278px] mx-auto flex flex-wrap justify-center gap-8 mt-[60px] px-2">
                    {whyChooseBoxes.map((box, index) => (
                        <div
                            key={index}
                            className="w-full max-w-[520px] md:max-w-none md:w-[calc(33.333%-22px)] bg-white rounded-lg p-6 shadow-md"
                            data-aos="fade-up"
                            data-aos-delay={index * 100}
                        >
                            <h3 className="font-karma font-bold text-hblack text-[18px] leading-[1.6]">
                                {box.title}
                            </h3>
                            <p className="font-inter text-font-grey text-[14px] leading-[1.57] mt-2">
                                {box.description}
                            </p>
                        </div>
                    ))}
                </div>

                <div className="flex justify-center mt-[60px]" data-aos="fade-up">
                    <Button name={"Work with us"} design={"filled"} action={"modal-open"} modalfunction={toggleModal} />
                </div>
            </div>

            {/* Where We Operate Section */}
            <section className="bg-white border-y border-[#E7E3DD] py-[60px] sm:py-[100px]">
                <div className="max-w-[1280px] mx-auto px-4 sm:px-8 lg:px-20 flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16">

                    <div className="w-full lg:w-[500px] shrink-0" data-aos="fade-up">
                        <h2 className="font-karma font-bold text-[#1A1A1A] text-[28px] leading-[1.25] sm:text-[34px] lg:text-[40px]">
                            Where we operate in Coimbatore
                        </h2>
                        <p className="font-inter text-font-grey text-[16px] leading-[1.63] mt-6">
                            Own land elsewhere in Tamil Nadu? Tell us where. We're expanding, and we'll let you know when we reach you.
                        </p>
                        <div className="mt-7">
                            <Button name={"Register my location"} design={"filled"} action={"modal-open"} modalfunction={() => setWaModal("register")} />
                        </div>
                    </div>

                    <div className="w-full flex justify-center" data-aos="fade-up">
                        <img
                            className="w-full max-w-[520px] h-auto rounded-xl"
                            src={serviceRadiusMap}
                            alt="Service radius map showing Coimbatore hub with Mettupalayam, Karamadai, Sirumugai belt, Walayar corridor, Thondamuthur and Anaikatti"
                        />
                    </div>

                </div>
            </section>

            {/* FAQ Section */}
            <FAQ />

            {/* Enquiry Form Modal */}
            <FormModal modal={modalToggle} active={toggleModal} />

            {/* WhatsApp Enquiry Modal - UI replicated from ChatusForm.js */}
            <div className={waModal ? "bg-black bg-opacity-70 inset-0 fixed z-50 w-full h-full flex flex-col items-center justify-center" : "hidden"}>
                <div className="bg-white px-5 py-4 rounded-lg relative w-[90%] max-w-md sm:max-w-lg lg:max-w-lg max-h-[90vh] p-6 sm:p-8 overflow-y-auto">
                    <img
                        src={closeIcon}
                        alt="close_icon"
                        className="w-[20px] h-[20px] sm:w-[20px] sm:h-[20px] absolute right-6 top-6 cursor-pointer z-50"
                        onClick={closeWaModal}
                    />

                    <div className="w-full h-full mx-auto sm:mt-[40px] sm:px-0">
                        <h2 className="text-center font-alibaba text-[18px] leading-[27px] sm:text-[20px] font-normal sm:leading-[32px] capitalize">
                            Connect With Us!
                        </h2>
                        <hr className="border-yellow border-[2px] w-[50px] lg:w-[87px] mx-auto sm:mt-5 sm:mb-3 my-3" />

                        <div>
                            <form className="flex flex-col gap-y-3 sm:gap-y-5" onSubmit={handleWaSubmit}>
                                <div>
                                    <p className="input-heading">
                                        Name <span className="text-[#FF0000]">*</span>
                                    </p>
                                    <input
                                        className="input-box"
                                        type="text"
                                        name="name"
                                        value={waFormValues.name}
                                        onChange={handleWaChange}
                                        onFocus={handleWaFocus}
                                    />
                                    <span className="error-msg">{waFormErrors.name}</span>
                                </div>

                                <div>
                                    <p className="input-heading">
                                        Phone <span className="text-[#FF0000]">*</span>
                                    </p>
                                    <input
                                        className="input-box"
                                        type="Phone"
                                        name="phonenumber"
                                        value={waFormValues.phonenumber}
                                        onChange={handleWaChange}
                                        onFocus={handleWaFocus}
                                        maxLength={10}
                                    />
                                    <span className="error-msg">{waFormErrors.phonenumber}</span>
                                </div>

                                {waModal === "register" && (
                                    <div>
                                        <p className="input-heading">
                                            Location of the Land <span className="text-[#FF0000]">*</span>
                                        </p>
                                        <input
                                            className="input-box"
                                            type="text"
                                            name="location"
                                            value={waFormValues.location}
                                            onChange={handleWaChange}
                                            onFocus={handleWaFocus}
                                        />
                                        <span className="error-msg">{waFormErrors.location}</span>
                                    </div>
                                )}

                                <Button name={"Submit"} design={"filled"} action={"form-submit"} />
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
